import { motion } from "framer-motion";

const colorForLetter = (s) => {
  // deterministic accent based on first char
  const palette = ["#60A5FA","#4ADE80","#FBBF24","#F87171","#A78BFA","#34D399","#F472B6","#FB923C"];
  const idx = (s?.charCodeAt(0) || 65) % palette.length;
  return palette[idx];
};

export default function TickerChip({ ticker, company, onClick, dragHandleProps, draggableProps, innerRef, isDragging }) {
  const initials = (company || ticker || "?").slice(0, 2).toUpperCase();
  const accent = colorForLetter(ticker || "A");
  // Use a known logo CDN; fall back to initials on error.
  const domain = guessDomain(company);
  const logoUrl = domain ? `https://logo.clearbit.com/${domain}` : null;

  return (
    <motion.div
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      initial={{ opacity: 0, y: -16, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className="ticker-chip"
      style={{
        transform: isDragging ? "rotate(-2deg) scale(1.04)" : undefined,
        boxShadow: isDragging ? `0 12px 32px ${accent}33` : undefined,
        ...draggableProps?.style,
      }}
      data-testid={`ticker-chip-${ticker}`}
    >
      <div className="ticker-logo" style={{ background: `${accent}22`, color: accent }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={ticker}
            onError={(e) => { e.target.style.display = "none"; e.target.parentElement.textContent = initials; }}
          />
        ) : (
          initials
        )}
      </div>
      <span className="text-sm font-bold text-white">${ticker}</span>
    </motion.div>
  );
}

function guessDomain(company) {
  if (!company) return null;
  const map = {
    "Apple": "apple.com",
    "Microsoft": "microsoft.com",
    "Alphabet": "google.com",
    "Google": "google.com",
    "Amazon": "amazon.com",
    "Meta Platforms": "meta.com",
    "Meta": "meta.com",
    "Tesla": "tesla.com",
    "Nvidia": "nvidia.com",
    "NVIDIA": "nvidia.com",
    "Berkshire Hathaway": "berkshirehathaway.com",
    "Lockheed Martin": "lockheedmartin.com",
    "Northrop Grumman": "northropgrumman.com",
    "Raytheon": "rtx.com",
    "RTX": "rtx.com",
    "Boeing": "boeing.com",
    "Rocket Lab": "rocketlabusa.com",
    "Iridium": "iridium.com",
    "Palantir": "palantir.com",
    "AeroVironment": "avinc.com",
    "Kratos": "kratosdefense.com",
    "Visa": "visa.com",
    "Mastercard": "mastercard.com",
    "PayPal": "paypal.com",
    "Block": "block.xyz",
    "Adyen": "adyen.com",
    "Shopify": "shopify.com",
    "SoFi": "sofi.com",
    "Affirm": "affirm.com",
    "Snowflake": "snowflake.com",
    "MongoDB": "mongodb.com",
    "Confluent": "confluent.io",
    "Datadog": "datadoghq.com",
    "Cloudflare": "cloudflare.com",
    "Roblox": "roblox.com",
    "Take-Two Interactive": "take2games.com",
    "Electronic Arts": "ea.com",
    "Sea Limited": "seagroup.com",
    "Adobe": "adobe.com",
    "Autodesk": "autodesk.com",
    "Salesforce": "salesforce.com",
    "Uber": "uber.com",
    "Lyft": "lyft.com",
    "Mobileye": "mobileye.com",
    "Aurora Innovation": "aurora.tech",
    "Pinterest": "pinterest.com",
    "Snap": "snap.com",
    "Reddit": "reddit.com",
    "Spotify": "spotify.com",
    "Etsy": "etsy.com",
    "Lululemon": "lululemon.com",
    "Nike": "nike.com",
    "Booking Holdings": "booking.com",
    "Airbnb": "airbnb.com",
    "Coinbase": "coinbase.com",
    "Robinhood": "robinhood.com",
    "Charles Schwab": "schwab.com",
    "JPMorgan Chase": "jpmorganchase.com",
    "JPMorgan": "jpmorganchase.com",
    "Goldman Sachs": "goldmansachs.com",
    "Capital One": "capitalone.com",
    "AMD": "amd.com",
    "Intel": "intel.com",
    "TSMC": "tsmc.com",
    "ASML": "asml.com",
    "ARM Holdings": "arm.com",
    "Arm Holdings": "arm.com",
    "Super Micro Computer": "supermicro.com",
    "Dell": "dell.com",
  };
  if (map[company]) return map[company];
  // best effort: lowercase strip
  const slug = company.toLowerCase().replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, "");
  if (!slug) return null;
  return `${slug}.com`;
}
